<template>
  <template v-for="(modal, index) in modalStack" :key="'relative-' + index">
    <div
      v-if="
        modal.props.isRelative &&
        shouldShowRelativeModal(index) &&
        !modal.props.target
      "
      class="modal-container-relative"
      :style="getContainerStyle(modal)"
    >
      <component
        :is="modal.component"
        v-bind="modal.props"
        @close-modal="(val) => closeModal(val ? index + val : index)"
        @open-modal="openModal"
        v-on="modal.listeners"
      />
    </div>
  </template>

  <teleport to="body" v-if="hasAbsoluteModal">
    <template v-for="(modal, index) in modalStack" :key="'absolute-' + index">
      <div
        @click="
          isCurrentModal(index) && !modal.props.noClose
            ? closeModal(index)
            : null
        "
        v-if="!modal.props.isRelative && shouldShowOverlay(index)"
        class="modal-overlay"
        :style="{
          '--overlay-background-color': getOverlayColor(modal),
          zIndex: modal.zIndex - 1,
        }"
      ></div>

      <div
        v-if="!modal.props.isRelative"
        :id="`modal-${modal.uniqueId}`"
        :data-component="getComponentName(modal.component)"
        :class="[
          'modal-container',
          'modal-container-absolute',
          {
            'is-visible': shouldModalBeVisible(index),
            'is-top': isCurrentModal(index),
          },
        ]"
        :style="{
          zIndex: modal.zIndex,
          ...getModalPosition(modal, index),
        }"
        @pointerdown="handlePointerDown($event, modal, index)"
        @pointermove="handlePointerMove($event, modal, index)"
        @pointerup="handlePointerUp($event, modal, index)"
        @pointercancel="handlePointerCancel($event, modal, index)"
        @click.self="!modal.props.noClose ? closeModal(index) : null"
      >
        <div
          class="modal-drag-shell"
          :style="getDragStyle(modal)"
          @click.capture="handleShellClick($event, modal.uniqueId)"
          @dragstart.prevent
        >
          <div :style="getContainerStyle(modal)">
            <component
              :is="modal.component"
              v-bind="modal.props"
              @close-modal="(val) => closeModal(val ? index + val : index)"
              @open-modal="openModal"
              v-on="modal.listeners"
            />
          </div>
        </div>
      </div>
    </template>
  </teleport>
</template>

<script>
let globalZIndexCounter = 10000;
let globalUniqueIdCounter = 0;
const Z_INDEX_STEP = 10;
</script>

<script setup>
import {
  ref,
  computed,
  markRaw,
  onMounted,
  onBeforeUnmount,
  useAttrs,
  nextTick,
  reactive,
} from "vue";

const modalStack = ref([]);
const dragStates = reactive({}); // key: modal.uniqueId
const emit = defineEmits([]);
const attrs = useAttrs();

const modalSizes = {
  auto: { width: "auto", height: "auto", padding: "0" },
  lg: { width: "51vw", height: "82vh", padding: "0" },
  lgP: { width: "51vw", height: "83.472vh", padding: "5.008vh 3.66vw" },
  md: { width: "44vw", height: "65vh", padding: "0" },
  sm: { width: "40vw", height: "47vh", padding: "0" },
  smP: { width: "40.264vw", height: "48.414vh", padding: "5.008vh 2.196vw" },
  xsm: { width: "30vw", height: "25vh", padding: "0" },
};

const screenWidth = ref(window.innerWidth);

// Computed: Verifica si hay modales absolutos en la pila
const hasAbsoluteModal = computed(() => {
  return modalStack.value.some((modal) => !modal.props.isRelative);
});

// Computed: Verifica si el modal actual tiene noUseAsLast
const hasCurrentModalNoUseAsLast = computed(() => {
  if (modalStack.value.length === 0) return false;
  const currentModal = modalStack.value[modalStack.value.length - 1];
  return currentModal.props.noUseAsLast;
});

// Computed: Encuentra el índice del último modal anterior sin noUseAsLast
const lastNonNoUseAsLastModalIndex = computed(() => {
  for (let i = modalStack.value.length - 2; i >= 0; i--) {
    const modal = modalStack.value[i];
    if (!modal.props.isRelative && !modal.props.noUseAsLast) {
      return i;
    }
  }
  return -1;
});

// Determina si un overlay debe mostrarse
const shouldShowOverlay = (index) => {
  if (isCurrentModal(index)) {
    return true;
  }

  if (
    hasCurrentModalNoUseAsLast.value &&
    index === lastNonNoUseAsLastModalIndex.value
  ) {
    return true;
  }

  return false;
};

// Determina si un modal relativo debe mostrarse
const shouldShowRelativeModal = (index) => {
  const modal = modalStack.value[index];
  return index === modalStack.value.length - 1 || modal.props.keepVisible;
};

const getComponentName = (component) => {
  if (component.name) return component.name;
  if (component.displayName) return component.displayName;
  if (component.__name) return component.__name;

  const componentString = component.toString();
  const match =
    componentString.match(/function\s+(\w+)/) ||
    componentString.match(/class\s+(\w+)/) ||
    componentString.match(/var\s+(\w+)/);

  return match ? match[1] : "UnknownComponent";
};

const updateWidth = () => {
  screenWidth.value = window.innerWidth;
};

onMounted(() => {
  window.addEventListener("resize", updateWidth);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateWidth);
});

const openModal = async (component, props = {}) => {
  const listeners = {};
  for (const key in attrs) {
    if (key.startsWith("on")) {
      const eventName = key.slice(2);
      listeners[eventName] = (...args) => {
        emit(eventName, ...args);
      };
    }
  }

  // Incrementamos el contador global cada vez que se abre un modal
  globalZIndexCounter += Z_INDEX_STEP;
  const currentZIndex = globalZIndexCounter;
  const uniqueId = ++globalUniqueIdCounter; // ID único para el DOM

  const componentName = getComponentName(component);

  const newModalObject = {
    component: markRaw(component),
    props: {
      ...props,
      isRelative: props.isRelative || false,
      keepVisible: props.keepVisible || false,
      noUseAsLast: props.noUseAsLast || false,
    },
    listeners,
    componentName,
    zIndex: currentZIndex, // Guardamos el Z-Index específico para este modal
    uniqueId: uniqueId, // ID único para manipular animaciones
  };

  modalStack.value.push(newModalObject);

  await nextTick();
  // Usamos el uniqueId en lugar del index para la animación
  await waitAnimation("opening", uniqueId, componentName);
};

const closeModal = async (indexToClose) => {
  const index = indexToClose ?? modalStack.value.length - 1;

  if (index < 0 || !modalStack.value[index]) return;

  const modal = modalStack.value[index];
  const componentName = modal.componentName || "";
  const uniqueId = modal.uniqueId;

  // Usamos el uniqueId para animar el cierre
  await waitAnimation("closing", uniqueId, componentName);

  if (indexToClose !== undefined && indexToClose !== null) {
    modalStack.value.splice(indexToClose, 1);
  } else {
    modalStack.value.pop();
  }

  if (dragStates[uniqueId]) {
    delete dragStates[uniqueId];
  }
};

// CAMBIO 3: Ajuste en la búsqueda del elemento para animar
const waitAnimation = async (clase, uniqueId, componentName = "") => {
  // Ahora buscamos por el ID único que asignamos en el template
  const modalId = `modal-${uniqueId}`;
  const modal = document.getElementById(modalId);

  if (!modal) {
    // Si no existe, no esperamos animación (modal relativo o ya desmontado)
    return;
  }

  if (componentName) {
    modal.setAttribute("data-component", componentName);
  }

  modal.classList.add(clase);

  const styles = window.getComputedStyle(modal);
  const duration = parseFloat(styles.animationDuration) || 0;
  const delay = parseFloat(styles.animationDelay) || 0;
  const total = (duration + delay) * 1000;

  const hasAnimation = styles.animationName !== "none" && total > 0;

  if (hasAnimation) {
    await new Promise((resolve) => setTimeout(resolve, total));
  }

  modal.classList.remove(clase);
  if (componentName) {
    modal.removeAttribute("data-component");
  }
};

const closeAll = () => {
  modalStack.value = [];
  for (const key in dragStates) {
    delete dragStates[key];
  }
};

const getContainerStyle = (modal) => {
  const sizeKey = modal?.props.size || "md";
  const size = modalSizes[sizeKey];

  let styles = {
    width: `${size.width}`,
    height: `${size.height}`,
    padding: size.padding,
    overflowY: modal?.props.scrollable ? "auto" : "visible",
  };

  return styles;
};

const getOverlayColor = (modal) => {
  const alpha = modal?.props.opacity ?? 0.5;
  return `rgba(0, 0, 0, ${alpha})`;
};

const isCurrentModal = (index) => {
  return index === modalStack.value.length - 1;
};

const shouldModalBeVisible = (index) => {
  const modal = modalStack.value[index];
  return isCurrentModal(index) || modal.props.keepVisible;
};

const getModalPosition = (modal, index) => {
  if (!shouldModalBeVisible(index)) return {};

  const position = modal.props.position || "center";
  const offsetX = modal.props.offsetX || 0;
  const offsetY = modal.props.offsetY || 0;

  if (modal.props.target) {
    const target = modal.props.target;

    if (
      !target ||
      !(target instanceof HTMLElement) ||
      !target.getBoundingClientRect
    ) {
      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
    }

    const rect = target.getBoundingClientRect();

    switch (position) {
      case "top":
        return {
          top: `${rect.top + offsetY}px`,
          left: `${rect.left + rect.width / 2 + offsetX}px`,
          transform: "translateX(-50%) translateY(-100%)",
        };

      case "bottom":
        return {
          top: `${rect.bottom + offsetY}px`,
          left: `${rect.left + rect.width / 2 + offsetX}px`,
          transform: "translateX(-50%)",
        };

      case "left":
        return {
          top: `${rect.top + rect.height / 2 + offsetY}px`,
          left: `${rect.left + offsetX}px`,
          transform: "translateX(-100%) translateY(-50%)",
        };

      case "right":
        return {
          top: `${rect.top + rect.height / 2 + offsetY}px`,
          left: `${rect.right + offsetX}px`,
          transform: "translateY(-50%)",
        };

      case "center":
      default:
        return {
          top: `${rect.top + rect.height / 2 + offsetY}px`,
          left: `${rect.right + offsetX}px`,
          transform: "translate(-50%, -50%)",
        };
    }
  }

  switch (position) {
    case "top":
      return {
        top: `${offsetY}px`,
        left: "50%",
        transform: "translateX(-50%)",
        "border-radius": "0 0 1.25rem 1.25rem",
      };

    case "bottom":
      return {
        bottom: `${offsetY}px`,
        left: "50%",
        transform: "translateX(-50%)",
        "border-radius": "1.25rem 1.25rem 0 0",
      };

    case "left":
      return {
        top: "50%",
        left: `${offsetX}px`,
        transform: "translateY(-50%)",
        "border-radius": "0 1.25rem 1.25rem 0",
      };

    case "right":
      return {
        top: "50%",
        right: `${offsetX}px`,
        transform: "translateY(-50%)",
        "border-radius": "1.25rem 0 0 1.25rem",
      };

    case "center":
    default:
      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
  }
};

// --- DRAG LOGIC ---
const initDragState = (uniqueId) => {
  if (!dragStates[uniqueId]) {
    dragStates[uniqueId] = {
      x: 0,
      y: 0,
      startX: 0,
      startY: 0,
      isDragging: false,
      isSnapping: false,
      pointerId: null,
      startTime: 0,
      hasMoved: false,
    };
  }
};

const getDragStyle = (modal) => {
  const state = dragStates[modal.uniqueId];
  if (!state) return {};

  return {
    transform: `translate3d(${state.x}px, ${state.y}px, 0)`,
    transition: state.isSnapping
      ? "transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)"
      : "none",
    userSelect: state.isDragging ? "none" : "auto",
  };
};

const isInteractiveElement = (target) => {
  if (!target) return false;
  return !!target.closest(
    "button, a, input, textarea, select, [contenteditable], .no-drag, .scrollable-area",
  );
};

const handleShellClick = (e, uniqueId) => {
  const state = dragStates[uniqueId];
  if (state && state.hasMoved) {
    e.stopPropagation();
    e.preventDefault();
    state.hasMoved = false; // reset
  }
};

const handlePointerDown = (e, modal, index) => {
  if (!modal.props.draggable) return;
  if (!isCurrentModal(index)) return; // Solo arrastrable si es el modal superior

  const handleSelector = modal.props.dragHandleSelector;
  if (handleSelector && !e.target.closest(handleSelector)) return;
  if (!handleSelector && isInteractiveElement(e.target)) return;

  initDragState(modal.uniqueId);
  const state = dragStates[modal.uniqueId];

  if (state.isSnapping) {
    state.isSnapping = false; // Frena animación si lo agarra a medias
  }

  state.isDragging = true;
  state.hasMoved = false;
  state.startX = e.clientX - state.x;
  state.startY = e.clientY - state.y;
  state.pointerId = e.pointerId;
  state.startTime = Date.now();

  e.target.setPointerCapture(e.pointerId);
};

const handlePointerMove = (e, modal) => {
  const state = dragStates[modal.uniqueId];
  if (!state || !state.isDragging || state.pointerId !== e.pointerId) return;

  const axis = modal.props.dragAxis || "auto";
  let allowedAxis = axis;
  const pos = modal.props.position || "center";
  if (allowedAxis === "auto") {
    if (pos === "top" || pos === "bottom") allowedAxis = "y";
    else if (pos === "left" || pos === "right") allowedAxis = "x";
    else allowedAxis = "both";
  }

  let newX = state.x;
  let newY = state.y;

  if (allowedAxis === "x" || allowedAxis === "both") {
    newX = e.clientX - state.startX;
    if (pos === "right") newX = Math.max(0, newX);
    if (pos === "left") newX = Math.min(0, newX);
  }
  if (allowedAxis === "y" || allowedAxis === "both") {
    newY = e.clientY - state.startY;
    if (pos === "bottom") newY = Math.max(0, newY);
    if (pos === "top") newY = Math.min(0, newY);
  }

  state.x = newX;
  state.y = newY;

  if (
    Math.abs(e.clientX - state.startX - state.x) > 3 ||
    Math.abs(e.clientY - state.startY - state.y) > 3
  ) {
    state.hasMoved = true;
  }
};

const handlePointerUp = (e, modal, index) => {
  const state = dragStates[modal.uniqueId];
  if (!state || !state.isDragging || state.pointerId !== e.pointerId) return;

  state.isDragging = false;
  e.target.releasePointerCapture(e.pointerId);

  const dismiss = modal.props.dragDismiss || { enabled: false }; // Drag Dismiss desactivado por defecto
  if (dismiss.enabled) {
    const minThresholdPx = dismiss.minThresholdPx || 72;
    const minVelocity = dismiss.minVelocity || 0.45;
    const timeDiff = Date.now() - state.startTime;

    const distanceX = state.x;
    const distanceY = state.y;
    const absX = Math.abs(distanceX);
    const absY = Math.abs(distanceY);

    const velocityX = timeDiff > 0 ? absX / timeDiff : 0;
    const velocityY = timeDiff > 0 ? absY / timeDiff : 0;

    const pos = modal.props.position || "center";
    let shouldClose = false;

    // Logica Hibrida Dismiss (Velocidad o Threshold de distancia)
    if (pos === "bottom" && distanceY > 0) {
      if (distanceY > minThresholdPx || velocityY > minVelocity)
        shouldClose = true;
    } else if (pos === "top" && distanceY < 0) {
      if (absY > minThresholdPx || velocityY > minVelocity) shouldClose = true;
    } else if (pos === "left" && distanceX < 0) {
      if (absX > minThresholdPx || velocityX > minVelocity) shouldClose = true;
    } else if (pos === "right" && distanceX > 0) {
      if (distanceX > minThresholdPx || velocityX > minVelocity)
        shouldClose = true;
    } else if (pos === "center") {
      if (
        absY > minThresholdPx ||
        velocityY > minVelocity ||
        absX > minThresholdPx ||
        velocityX > minVelocity
      ) {
        shouldClose = true;
      }
    }

    if (shouldClose) {
      closeModal(index);
      return;
    }

    // Snap back a (0,0) si calificaba para cierre pero no alcanzó el umbral
    state.isSnapping = true;
    state.x = 0;
    state.y = 0;

    setTimeout(() => {
      if (state && !state.isDragging) state.isSnapping = false;
    }, 300);
  }
  // Si dragDismiss.enabled = false, el modal se queda donde lo soltaron (draggable libre)
};

const handlePointerCancel = (e, modal, index) => {
  handlePointerUp(e, modal, index);
};

defineExpose({
  openModal,
  closeModal,
  closeAll,
});
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  /* El z-index ahora se maneja en linea mediante estilo dinámico */
  background-color: var(--overlay-background-color, rgba(0, 0, 0, 0.6));
}

.modal-container {
  opacity: 0;
  pointer-events: none;
  overflow: visible;
  touch-action: none;
}

.modal-drag-shell {
  width: 100%;
  height: 100%;
  will-change: transform;
  background: var(--bg-popup);
  border-radius: 1.25rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  touch-action: none;
  /* Ensures mobile gestures are captured by drag logic instead of scrolling */
}

.modal-container.is-visible {
  opacity: 1;
}

.modal-container.is-top {
  pointer-events: auto;
}

.modal-container-relative {
  width: 100%;
  height: 100%;
  background: transparent;
  border-radius: 1.25rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  scrollbar-width: none;
}

.modal-container-absolute {
  position: fixed;
}
@media (max-width: 768px) {
  .modal-drag-shell {
    border-radius: 1.25rem;
  }
}
</style>
